import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ContentPageHeader, MetricGrid, SummaryMetricCard } from '@/components/global';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { DollarSign, Building2, Clock, Download, MoreHorizontal, Check, CreditCard } from 'lucide-react';
import { downloadCsv } from '@/lib/csvExport';

type CommissionStatus = 'PENDING' | 'APPROVED' | 'PAID';

interface VenueCommission {
  venueId: string;
  venueName: string;
  commissionType: 'REVENUE_SHARE' | 'FIXED_MONTHLY';
  commissionRate: number | null;
  fixedAmount: number | null;
  grossRevenue: number;
  transactionCount: number;
  calculatedAmount: number;
  finalAmount: number;
  status: CommissionStatus;
  periodStart: string;
  periodEnd: string;
  paymentId: string;
}

interface CommissionListResult {
  items: VenueCommission[];
  total: number;
  page: number;
  pageSize: number;
  summary: {
    totalPending: number;
    totalApproved: number;
    totalPaid: number;
    totalDue: number;
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: CommissionStatus }) {
  const variants: Record<CommissionStatus, { variant: 'default' | 'secondary' | 'outline'; label: string }> = {
    PENDING: { variant: 'secondary', label: 'Pending' },
    APPROVED: { variant: 'default', label: 'Approved' },
    PAID: { variant: 'outline', label: 'Paid' },
  };

  const { variant, label } = variants[status];

  return <Badge variant={variant}>{label}</Badge>;
}

export default function VenueCommissionsPage() {
  const { dateRange, setDateRange } = useDateRange('MTD');
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<CommissionListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const pageSize = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      params.set('periodStart', dateRange.startDate.toISOString());
      params.set('periodEnd', dateRange.endDate.toISOString());

      const res = await apiFetch(`/api/v1/finance/commissions?${params}`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error loading commissions:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [statusFilter, page, setSearchParams]);

  async function handleApprove(paymentId: string) {
    try {
      const res = await apiFetch(`/api/v1/finance/commissions/${paymentId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error approving commission:', error);
    }
  }

  async function handlePay(paymentId: string) {
    try {
      const res = await apiFetch(`/api/v1/finance/commissions/${paymentId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error marking commission as paid:', error);
    }
  }

  async function handleCalculate() {
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      });
      const res = await apiFetch(`/api/v1/finance/commissions/calculate?${params}`, {
        method: 'POST',
      });
      if (res.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error calculating commissions:', error);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  function exportCsv() {
    if (!data || data.items.length === 0) return;
    downloadCsv(
      `venue-commissions-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Venue',
        'Period start',
        'Period end',
        'Type',
        'Rate',
        'Revenue (GBP)',
        'Commission (GBP)',
        'Status',
      ],
      data.items.map((commission) => [
        commission.venueName,
        commission.periodStart,
        commission.periodEnd,
        commission.commissionType === 'REVENUE_SHARE' ? 'Revenue Share' : 'Fixed',
        commission.commissionType === 'REVENUE_SHARE'
          ? `${commission.commissionRate ?? 0}%`
          : (commission.fixedAmount ?? 0).toFixed(2),
        commission.grossRevenue.toFixed(2),
        commission.finalAmount.toFixed(2),
        commission.status,
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <ContentPageHeader
          title="Venue Commissions"
          actions={
            <>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full sm:w-auto"
              />
              <Button variant="outline" onClick={handleCalculate} className="w-full sm:w-auto">
                Calculate Period
              </Button>
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={loading || !data || data.items.length === 0}
                className="w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </>
          }
        />

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Total Payable"
            value={data ? formatCurrency(data.summary.totalDue) : '-'}
            icon={DollarSign}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Venues"
            value={data ? data.items.length.toString() : '-'}
            icon={Building2}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Pending"
            value={data ? formatCurrency(data.summary.totalPending) : '-'}
            icon={Clock}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Paid"
            value={data ? formatCurrency(data.summary.totalPaid) : '-'}
            icon={CreditCard}
            isLoading={loading}
          />
        </MetricGrid>

        <Card className="mb-6">
          <div className="p-4 flex items-center gap-4 border-b">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Venue</TableHead>
                <TableHead className="hidden md:table-cell">Period</TableHead>
                <TableHead className="hidden lg:table-cell">Type</TableHead>
                <TableHead className="hidden text-right lg:table-cell">Rate</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Revenue</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : data?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No commissions found
                  </TableCell>
                </TableRow>
              ) : (
                data?.items.map((commission) => (
                  <TableRow key={commission.paymentId}>
                    <TableCell className="font-medium">{commission.venueName}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDate(commission.periodStart)} - {formatDate(commission.periodEnd)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {commission.commissionType === 'REVENUE_SHARE' ? 'Revenue Share' : 'Fixed'}
                    </TableCell>
                    <TableCell className="hidden text-right lg:table-cell">
                      {commission.commissionType === 'REVENUE_SHARE'
                        ? `${commission.commissionRate}%`
                        : formatCurrency(commission.fixedAmount || 0)}
                    </TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{formatCurrency(commission.grossRevenue)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(commission.finalAmount)}</TableCell>
                    <TableCell>
                      <StatusBadge status={commission.status} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {commission.status === 'PENDING' && (
                            <DropdownMenuItem onClick={() => handleApprove(commission.paymentId)}>
                              <Check className="h-4 w-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                          )}
                          {commission.status === 'APPROVED' && (
                            <DropdownMenuItem onClick={() => handlePay(commission.paymentId)}>
                              <CreditCard className="h-4 w-4 mr-2" />
                              Mark Paid
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} • {data?.total || 0} total
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
